<template>

    <div v-if="user" id="layout-app" :class="{ 'layout': true }">
        <v-the-alerts />
        <v-the-cookie-banner />
        <v-the-checklist />
        <transition name="modal">
            <v-modal-logout v-if="modal === 'logout'" />
            <v-modal-project-activate v-if="modal === 'project-activate'" />
            <v-modal-project-create v-if="modal === 'project-create'" />
            <v-modal-review-create v-if="modal === 'review-create-quick'" @saved="_refreshProject" />
            <v-modal-location-create v-if="modal === 'location-create-quick'" @saved="_refreshProject" />
            <v-modal-tag-create v-if="modal === 'tag-create-quick'" @saved="_refreshProject" />
            <v-modal-customer-create v-if="modal === 'customer-create-quick'" />
            <v-modal-cookie-preferences v-if="modal === 'cookie-preferences'" />
        </transition>
        <v-the-agency-banner />
        <div class="layout-body">
            <div :class="`secondary ${$store.getters.sidebarState}`">
                <v-the-sidebar />
            </div>
            <div class="primary">
                <v-the-operations-banner />
                <transition name="fade" mode="out-in">
                    <router-view :key="(project && project.id) + '-' + ($store.getters.locationId || '')"></router-view>
                </transition>
                <v-the-legal />
            </div>
        </div>
    </div>

</template>

<style scoped lang="scss">

    html, body {
        height: 100%;
    }

    .layout {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-width: map-get($grid-breakpoints, 'xl');
        .layout-body {
            display: flex;
            flex: 1 1 0;
            min-height: 0;
            overflow: hidden;
        }
        .secondary {
            flex-shrink: 0;
            min-height: 0;
            overflow: hidden;
            &.collapsed {
                width: $sidebar-width-collapsed;
            }
            &.expanded {
                width: $sidebar-width-expanded;
            }
        }
        .primary {
            background: $color-bg;
            display: flex;
            flex: 1 1 0;
            flex-direction: column;
            min-height: 0;
            min-width: 0;
            overflow: hidden;
            position: relative;
        }
    }

</style>

<script>

import ConnectionApi from '@api/ConnectionApi';
import ModalCookiePreferences from '@modals/ModalCookiePreferences';
import ModalCustomerCreate from '@modals/ModalCustomerCreate';
import ModalLocationCreate from '@modals/ModalLocationCreate';
import ModalLogout from '@modals/ModalLogout';
import ModalProjectActivate from '@modals/ModalProjectActivate';
import ModalProjectCreate from '@modals/ModalProjectCreate';
import ModalReviewCreate from '@modals/ModalReviewCreate';
import ModalTagCreate from '@modals/ModalTagCreate';
import moment from 'moment';
import PusherUtil from '@utils/PusherUtil';
import { setupRoute } from '@utils/SetupUtil';
import ThemeUtil from '@utils/ThemeUtil';
import TheAgencyBanner from '@cmp/TheAgencyBanner';
import TheAlerts from '@cmp/TheAlerts';
import TheCookieBanner from '@cmp/TheCookieBanner';
import TheChecklist from '@cmp/TheChecklist';
import TheLegal from '@cmp/TheLegal';
import TheOperationsBanner from '@cmp/TheOperationsBanner';
import TheSidebar from '@cmp/TheSidebar';

export default {

    components: {
        'v-modal-cookie-preferences': ModalCookiePreferences,
        'v-modal-customer-create': ModalCustomerCreate,
        'v-modal-location-create': ModalLocationCreate,
        'v-modal-logout': ModalLogout,
        'v-modal-project-activate': ModalProjectActivate,
        'v-modal-project-create': ModalProjectCreate,
        'v-modal-review-create': ModalReviewCreate,
        'v-modal-tag-create': ModalTagCreate,
        'v-the-agency-banner': TheAgencyBanner,
        'v-the-alerts': TheAlerts,
        'v-the-cookie-banner': TheCookieBanner,
        'v-the-checklist': TheChecklist,
        'v-the-legal': TheLegal,
        'v-the-operations-banner': TheOperationsBanner,
        'v-the-sidebar': TheSidebar,
    },

    computed: {

        agency() {
            return this.$store.getters.agency;
        },

        user() {
            return this.$store.getters.user;
        },

        sidebar() {
            return this.$store.getters.sidebar;
        },

        isDarkMode() {
            return this.$store.getters.isDarkMode;
        },

        hasFunctionalConsent() {
            return this.$store.getters.hasFunctionalConsent;
        },

    },

    beforeMount() {
        const script = document.createElement('script');
        script.src = 'https://do.featurebase.app/js/sdk.js';
        script.id = 'featurebase-sdk';
        document.head.appendChild(script);
    },

    beforeDestroy() {
        ThemeUtil.unsetThemeForUser(this.user);
        PusherUtil.disconnect();
        PusherUtil.leave(`user.${this.user.id}`);
        if (this.project) {
            PusherUtil.leave(`project.${this.project.id}`);
        }
    },

    mounted() {
        // Set the theme.
        ThemeUtil.setThemeForUser(this.user);
        if (this.user.agency && typeof this.user.agency === 'object' && !Array.isArray(this.user.agency)) {
            ThemeUtil.setThemeForAgency(this.user.agency);
        }

        // Connect to Pusher.
        PusherUtil.connect();
        window.Echo.private(`user.${this.user.id}`)
            .listen('.user.updated', e => {
                this._refreshUser();
                this._refreshProject();
            })
            .listen('.user.alert.created', e => {
                this.$store.commit('setAlert', {
                    message: e.message,
                    type: e.type,
                });
                const operation = this.operationForAlert(e.key);
                if (!operation) {
                    return;
                }
                // Connection sync banners are matched by connectionId via
                // user.operation.updated — keep alerts generic.
                if (operation.types.includes('connection_sync')) {
                    return;
                }
                const payload = {
                    message: e.message,
                    projectId: e.project_id || null,
                    types: operation.types,
                };
                if (operation.status === 'success') {
                    this.$store.commit('completeOperation', payload);
                } else {
                    this.$store.commit('failOperation', payload);
                }
            })
            .listen('.user.operation.updated', e => {
                // Require type — without it store matchers can hit the wrong op.
                if (!e.type) {
                    return;
                }
                const payload = {
                    message: e.message || null,
                    projectId: e.project_id || null,
                    connectionId: e.connection_id || null,
                    types: [e.type],
                };
                if (e.status === 'success') {
                    this.$store.commit('completeOperation', payload);
                } else {
                    this.$store.commit('failOperation', payload);
                }
            });

        // Setup seeds connection_sync on LayoutAuth before this listener exists.
        // Reconcile any ops whose push event already fired.
        this.reconcileInProgressOperations();

        // Initialize HelpScout.
        this.initHelpScout();

        // Initialize FeatureBase.
        this.initFeatureBase();
    },

    watch: {

        project: {
            immediate: true,
            handler(newProject, oldProject) {
                if (oldProject && newProject && newProject.id !== oldProject.id) {
                    PusherUtil.leave(`project.${oldProject.id}`);
                    this.setupProjectListener();
                } else if (oldProject === undefined) {
                    this.setupProjectListener();
                }
                // Store updates (e.g. project switch) may not navigate; enforce setup / picker.
                // Sub-accounts never use the wizard.
                if (this.user.is_sub_account && !newProject) {
                    this.$router.push({ name: 'select-project' }).catch(() => {});
                } else if (!this.user.is_sub_account && this.$route.name !== 'setup' && (!newProject || !newProject.setup_completed_at)) {
                    this.$router.push(setupRoute({
                        from: this.$route,
                        fullPath: this.$route.fullPath,
                    })).catch(() => {});
                }
            },
        },

        hasFunctionalConsent(val) {
            if (val) this.initHelpScout();
        },

    },

    methods: {

        initFeatureBase() {
            if (this.user.is_sub_account) return;
            if (typeof window.Featurebase !== 'function') {
                window.Featurebase = function() {
                    (window.Featurebase.q = window.Featurebase.q || []).push(arguments);
                }
            }
            if (this.user.featurebase_jwt) {
                window.Featurebase('identify', {
                    organization: 'moregoodreviews',
                    featurebaseJwt: this.user.featurebase_jwt,
                });
            }
            window.Featurebase('initialize_changelog_widget', {
                organization: 'moregoodreviews',
                placement: 'right',
                theme: this.isDarkMode ? 'dark' : 'light',
                fullscreenPopup: true,
                usersName: this.user.first_name,
            });
            window.Featurebase('initialize_survey_widget', {
                organization: 'moregoodreviews',
                placement: 'bottom-right',
                theme: this.isDarkMode ? 'dark' : 'light',
            });
        },

        initHelpScout() {
            if (this.user.is_sub_account || !this.project || this.project.is_demo) return;
            const domain = window.location.hostname.toLowerCase().replace('www.', '');
            if (domain === process.env.VUE_APP_DOMAIN) {
                if (typeof window.Beacon !== 'undefined') {
                    window.Beacon('init', process.env.VUE_APP_HELPSCOUT_KEY);
                    window.Beacon('identify', {
                        name: this.user.name,
                        email: this.user.email,
                        signature: this.user.helpscout_signature,
                        user_id: String(this.user.id),
                        plan: this.user.plan ? this.user.plan.slug : 'free',
                        billing_id: this.user.billing_id !== null ? this.user.billing_id : '',
                        billing_plan: this.user.billing_plan !== null ? this.user.billing_plan : '',
                        billing_status: this.user.billing_status !== null ? this.user.billing_status : '',
                        agency: this.user.features.agency ? 'Yes' : 'No',
                        signed_up_at: moment.unix(this.user.created_at).format('YYYY-MM-DD'),
                        trial_ends_at: this.user.trial_ends_at ? moment.unix(this.user.trial_ends_at).format('YYYY-MM-DD') : '',
                        subscribed_at: this.user.subscribed_at ? moment.unix(this.user.subscribed_at).format('YYYY-MM-DD') : '',
                    });
                }
            }
        },

        operationForAlert(key) {
            return {
                connection_disabled: { status: 'failed', types: ['connection_sync'] },
                customer_import_completed: { status: 'success', types: ['customer_import'] },
                customer_import_failed: { status: 'failed', types: ['customer_import'] },
                review_import_completed: { status: 'success', types: ['review_import'] },
                review_import_failed: { status: 'failed', types: ['review_import'] },
                scrape_completed: { status: 'success', types: ['review_scrape'] },
                scrape_failed: { status: 'failed', types: ['review_scrape'] },
                sync_completed: { status: 'success', types: ['connection_sync'] },
                sync_failed: { status: 'failed', types: ['connection_sync'] },
            }[key] || null;
        },

        reconcileInProgressOperations() {
            // Setup seeds connection_sync on LayoutAuth before Echo listeners exist.
            // Catch up from connection timestamps: success sets sync_completed_at;
            // failure clears sync_scheduled_at and leaves sync_completed_at null.
            // Still-running syncs keep sync_scheduled_at and no newer completed_at.
            const ops = this.$store.getters.operations.filter(o => (
                o.status === 'progress' &&
                o.type === 'connection_sync' &&
                o.connectionId
            ));
            ops.forEach((op) => {
                ConnectionApi.getConnection(op.connectionId)
                    .then((response) => {
                        const current = this.$store.getters.operations.find(o => o.key === op.key);
                        if (!current || current.status !== 'progress') {
                            return;
                        }
                        const conn = response.data.data;
                        const payload = {
                            connectionId: op.connectionId,
                            projectId: op.projectId,
                            types: ['connection_sync'],
                        };
                        if (
                            conn.sync_completed_at &&
                            (
                                !conn.sync_scheduled_at ||
                                conn.sync_completed_at >= conn.sync_scheduled_at
                            )
                        ) {
                            this.$store.commit('completeOperation', payload);
                            return;
                        }
                        // Failed attempt: schedule cleared, completed never set.
                        // Op is only seeded after sync() returned (scheduled was set),
                        // so both-null means the attempt already finished as a failure.
                        if (!conn.sync_scheduled_at && !conn.sync_completed_at) {
                            this.$store.commit('failOperation', payload);
                        }
                    })
                    .catch(() => {});
            });
        },

        setupProjectListener() {
            if (!this.project) return;
            this.$nextTick(() => {
                window.Echo.private(`project.${this.project.id}`)
                    .listen('.project.updated', e => {
                        // Only refresh if this is the currently selected project.
                        if (e.id === this.project.id) {
                            this._refreshProject();
                        }
                    });
            });
        },

    },

}

</script>